import { styled, alpha } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

export const ScannerWrapper = styled(Stack)`
  width: 100%;
  gap: 24px;
`;

export const TopBarContainer = styled(Stack)(({ theme }) => ({
  width: '100%',
  justifyContent: 'space-between',
  gap: '16px',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
    alignItems: 'center',
  },
}));

export const TitleActionsWrapper = styled(Stack)`
  flex-direction: row;
  gap: 16px;
  align-items: center;
  flex: 1;
`;

export const ButtonContentStack = styled(Stack)`
  flex-direction: row;
  align-items: center;
  gap: 6px;
`;

export const LoadingBox = styled('div')`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: row;
  height: 120px;
  gap: 14px;
`;

// Band Selector

export const ScannerToggleButtonGroup = styled(ToggleButtonGroup)`
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid
    ${({ theme }) =>
      theme.palette.mode === 'dark'
        ? theme.palette.grey['02']
        : theme.palette.primary.main};
  height: 42px;

  .MuiToggleButtonGroup-grouped {
    border: none;
    &:not(:first-of-type) {
      border-left: 1px solid
        ${({ theme }) =>
          theme.palette.mode === 'dark'
            ? theme.palette.transparent.tertiary
            : theme.palette.primary.main};
    }
  }
`;

export const ScannerToggleButton = styled(ToggleButton)`
  padding: 6px 16px;
  font-size: 13px;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0.02em;
  color: ${({ theme }) => theme.palette.primary.main};
  transition: all 0.25s ease;
  white-space: nowrap;

  @media (max-width: 600px) {
    padding: 6px 10px;
    font-size: 11px;
  }

  &.Mui-selected {
    color: ${({ theme }) => theme.palette.primary.main};
    font-weight: 700;
    background: ${({ theme }) => alpha(theme.palette.primary.main, 0.12)};
  }

  &.Mui-selected:hover {
    background: ${({ theme }) => alpha(theme.palette.primary.main, 0.18)};
  }

  &:hover {
    background: ${({ theme }) =>
      theme.palette.mode === 'dark'
        ? theme.palette.transparent.tertiary
        : theme.palette.transparent.quaternary};
  }
`;

export const BandCountChip = styled(Chip)<{ active: boolean }>(
  ({ active }) => `
  margin-left: 8px;
  height: 18px;
  font-size: 10px;
  color: inherit;
  background: ${active ? 'rgba(255,255,255,0.2)' : 'transparent'};
`
);

// Quality

export const QUALITY_COLORS: Record<QualityLevel, string> = {
  otimo: '#4CAF50',
  bom: '#00CFFF',
  regular: '#F59E0B',
  congestionado: '#F44336',
};

export const QUALITY_LABELS: Record<QualityLevel, string> = {
  otimo: 'Ótimo',
  bom: 'Bom',
  regular: 'Regular',
  congestionado: 'Congestionado',
};

interface QualityDotProps {
  quality: QualityLevel;
}

export const QualityDot = styled('span')<QualityDotProps>(
  ({ quality }) => `
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${QUALITY_COLORS[quality]};
  flex-shrink: 0;
`
);

export const LegendRow = styled(Stack)`
  flex-direction: row;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

export const LegendItem = styled(Stack)`
  flex-direction: row;
  align-items: center;
  gap: 6px;
`;

// Hidden SSID

export const HiddenSSIDLabel = styled('span')`
  font-style: italic;
  opacity: 0.6;
  font-size: 12px;
`;

// DFS Badge

export const DFSBadge = styled('span')`
  font-size: 9px;
  font-weight: 700;
  padding: 1px 4px;
  border-radius: 3px;
  background: rgba(245, 158, 11, 0.2);
  color: #f59e0b;
  border: 1px solid rgba(245, 158, 11, 0.4);
  margin-left: 4px;
  vertical-align: middle;
  letter-spacing: 0.5px;
`;

// Error State

export const ErrorStateWrapper = styled(Stack)`
  width: 100%;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  gap: 16px;
  text-align: center;
`;

// Section title

export const SectionTitle = styled('div')`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.white['06']};
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 4px;
`;

export const CongestionListContainer = styled(Stack)(({ theme }) => ({
  minWidth: '100%',
  borderBottom: `1px solid ${theme.palette.grey['02']}`,
  paddingBottom: '20px',
  [theme.breakpoints.up('lg')]: {
    minWidth: 260,
    width: 260,
    maxWidth: 280,
    borderRight: `1px solid ${theme.palette.grey['02']}`,
    borderBottom: 'none',
    paddingRight: '12px',
    paddingBottom: 0,
  },
}));

export const CongestionListScrollable = styled(Stack)(({ theme }) => ({
  maxHeight: 320,
  [theme.breakpoints.up('sm')]: {
    maxHeight: 380,
  },
  [theme.breakpoints.up('lg')]: {
    maxHeight: 520,
  },
  overflowY: 'auto',
  paddingRight: '8px',
  '&::-webkit-scrollbar': { width: '4px' },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.grey['03'],
    borderRadius: '4px',
  },
}));

export const CongestionChannelLabel = styled(Typography)(({ theme }) => ({
  width: '60px',
  color: theme.palette.white['06'],
}));

export const CongestionBarWrapper = styled(Box)({
  flex: 1,
  marginLeft: '16px',
  marginRight: '16px',
  height: '4px',
  background: 'rgba(255,255,255,0.05)',
  borderRadius: '2px',
  position: 'relative',
});

export const CongestionBarFill = styled(Box)<{
  fillcolor: string;
  fillwidth: string;
}>(({ fillcolor, fillwidth }) => ({
  position: 'absolute',
  left: 0,
  top: 0,
  height: '100%',
  width: fillwidth,
  background: fillcolor,
  borderRadius: '2px',
}));

export const CongestionCountLabel = styled(Typography)(({ theme }) => ({
  width: '30px',
  textAlign: 'right',
  color: theme.palette.white['06'],
}));

// Added via refactoring

export const ScanActionsStack = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '12px',
  flexWrap: 'wrap',
});

export const ScanTimeInfoStack = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '6px',
});

export const BandHeaderGrid = styled(Stack)({
  flexDirection: 'row',
  width: '100%',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: '24px',
  display: 'grid',
  gridTemplateColumns: '0.25fr 1.75fr',
});

export const BandSelectorStack = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '12px',
  width: '100%',
});

export const BandPanelLayout = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  gap: '16px',
  width: '100%',
  alignItems: 'stretch',
  [theme.breakpoints.up('lg')]: {
    flexDirection: 'row',
    gap: '12px',
  },
}));

export const ChartWrapper = styled(Box)({
  flex: 1,
  minWidth: 0,
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
});

export const BandPanelHeader = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
});

export const CongestionListHeader = styled(Stack)({
  flexDirection: 'row',
  justifyContent: 'space-between',
  marginBottom: '16px',
  paddingRight: '12px',
});

export const CongestionListItem = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
});

export const SignalCellStack = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '10px',
  color: theme.palette.white['06'],
  width: '100%',
  minWidth: 0,
}));

export const LoadingTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.white['06'],
  fontSize: '16px',
  fontWeight: 700,
  lineHeight: 1.3,
}));

export const LoadingSubtitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.white['06'],
  opacity: 0.85,
}));

export const EllipsisTypography = styled(Typography)({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  display: 'block',
  width: '100%',
});

export const InterferenceDot = styled(Box)<{ color: string; size?: number }>(
  ({ color, size = 8 }) => ({
    width: size,
    height: size,
    borderRadius: '50%',
    backgroundColor: color,
    boxShadow: `0 0 6px ${color}`,
    flexShrink: 0,
  })
);

export const PopperHeaderStack = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  paddingBottom: '8px',
  borderBottom: `1px solid ${
    theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.08)'
      : 'rgba(0, 0, 0, 0.08)'
  }`,
}));

export const SignalBarWrapper = styled(Box)({
  flex: 1,
  minWidth: '40px',
  height: '6px',
  borderRadius: '3px',
  background: 'rgba(255, 255, 255, 0.08)',
  position: 'relative',
});

export const SignalDbmTypography = styled(Typography)<{ color: string }>(
  ({ color }) => ({
    color,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    minWidth: '60px',
    textAlign: 'right',
  })
);

/** Typography for column headers in congestion list */
export const CongestionHeaderLabel = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.white['06'],
}));

/** Monospace BSSID cell text with nowrap */
export const BssidCellTypography = styled(Typography)(({ theme }) => ({
  fontFamily: 'monospace',
  color: theme.palette.white['06'],
  whiteSpace: 'nowrap',
}));

/** Channel cell text in networks table */
export const ChannelCellTypography = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.white['06'],
  whiteSpace: 'nowrap',
}));

/** Bandwidth cell text in networks table */
export const BandwidthCellTypography = styled(Typography)(({ theme }) => ({
  color: theme.palette.white['06'],
  whiteSpace: 'nowrap',
}));

/** Standard cell text in networks table */
export const StandardCellTypography = styled(Typography)(({ theme }) => ({
  color: theme.palette.white['06'],
  whiteSpace: 'nowrap',
}));

/** General history table white cell text */
/** Flex row layout with smaller gap for column headers */
export const TableColumnHeaderStack = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '6px',
});

/** Row item inside interference scale description popper */
export const InterferenceItemRow = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: '8px',
});

/** Warning card header row */
export const WarningModalHeader = styled(Stack)({
  flexDirection: 'row',
  gap: '8px',
  marginBottom: '8px',
});

/** Scanner top bar title container */
export const TopBarTitleStack = styled(Stack)({
  alignItems: 'flex-start',
  gap: '4px',
});

/** Scanner top bar title row with icon/popper */
export const TopBarTitleRow = styled(Stack)({
  flexDirection: 'row',
  gap: '8px',
});

/** Loading description container */
export const LoadingTextStack = styled(Stack)({
  alignItems: 'flex-start',
  textAlign: 'left',
  gap: '2px',
});

/** SectionTitle without bottom margin for band panel header */
export const BandSectionTitle = styled(SectionTitle)({
  marginBottom: 0,
});

/** Table container inside band panel card */
export const BandTableContainerStack = styled(Stack)({
  gap: '16px',
  width: '100%',
});

/** Icon wrapper for toggle buttons */
